import { Button, Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  historyColumnLabelKey,
  TPP_HISTORY_ALL_COLUMNS,
  type TppHistoryColumnId,
} from '../../../utils/tppHistoryTableLayout';

type TppHistoryColumnsModalProps = {
  show: boolean;
  onHide: () => void;
  draftColumns: TppHistoryColumnId[];
  onToggleColumn: (col: TppHistoryColumnId, checked: boolean) => void;
  onMoveColumn: (col: TppHistoryColumnId, dir: -1 | 1) => void;
  onSave: () => void;
  onResetDraft: () => void;
};

export default function TppHistoryColumnsModal({
  show,
  onHide,
  draftColumns,
  onToggleColumn,
  onMoveColumn,
  onSave,
  onResetDraft,
}: TppHistoryColumnsModalProps) {
  const { t } = useTranslation();

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton closeLabel={t('common.close')}>
        <Modal.Title>{t('tpp.history_columns_title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">{t('tpp.history_columns_hint')}</p>
        <ul className="list-unstyled mb-0">
          {TPP_HISTORY_ALL_COLUMNS.map((col) => {
            const inDraft = draftColumns.includes(col);
            const draftIdx = draftColumns.indexOf(col);
            return (
              <li key={col} className="d-flex align-items-center gap-2 py-1 border-bottom">
                <Form.Check
                  type="checkbox"
                  id={`tpp-history-col-${col}`}
                  label={t(historyColumnLabelKey(col))}
                  checked={inDraft}
                  onChange={(e) => onToggleColumn(col, e.target.checked)}
                />
                {inDraft && (
                  <div className="ms-auto d-flex gap-1">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={draftIdx === 0}
                      onClick={() => onMoveColumn(col, -1)}
                      aria-label={t('tpp.history_columns_move_up')}
                    >
                      <i className="fas fa-arrow-up" aria-hidden />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={draftIdx === draftColumns.length - 1}
                      onClick={() => onMoveColumn(col, 1)}
                      aria-label={t('tpp.history_columns_move_down')}
                    >
                      <i className="fas fa-arrow-down" aria-hidden />
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onResetDraft}>
          {t('tpp.history_columns_reset')}
        </Button>
        <Button variant="secondary" onClick={onHide}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" onClick={onSave}>
          {t('common.save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
